import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminCategoriesApi } from '../api';
import { useTelegram } from '../hooks/useTelegram';
import { Loading } from '../components/Loading';
import type { Category } from '../types';
import './AdminCategoriesPage.css';

export const AdminCategoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const { tg } = useTelegram();

  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingCategoryId, setDeletingCategoryId] = useState<number | null>(null);
  const [reorderingCategoryId, setReorderingCategoryId] = useState<number | null>(null);

   
  useEffect(() => {
    // Show back button
    if (tg?.BackButton) {
      tg.BackButton.show();
      tg.BackButton.onClick(handleBack);
    }

    return () => {
      if (tg?.BackButton) {
        tg.BackButton.hide();
        tg.BackButton.offClick(handleBack);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tg]);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await adminCategoriesApi.getCategories();
      // Sort by sort_order
      const sorted = response.categories.sort((a, b) => a.sort_order - b.sort_order);
      setCategories(sorted);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setError(err instanceof Error ? err.message : 'Не удалось загрузить категории');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin');
  };

  const handleAddCategory = () => {
    navigate('/admin/categories/create');
  };

  const handleCategoryClick = (categoryId: number) => {
    navigate(`/admin/categories/${categoryId}/edit`);
  };

  const handleReorder = async (e: React.MouseEvent, categoryId: number, direction: 'up' | 'down') => {
    e.stopPropagation(); // Prevent opening category edit page

    try {
      setReorderingCategoryId(categoryId);

      // Call API to reorder
      await adminCategoriesApi.reorderCategory(categoryId, direction);

      // Reload categories to get updated sort_order
      const response = await adminCategoriesApi.getCategories();
      const sorted = response.categories.sort((a, b) => a.sort_order - b.sort_order);
      setCategories(sorted);

      if (tg?.HapticFeedback) {
        tg.HapticFeedback.impactOccurred('light');
      }
    } catch (err: unknown) {
      console.error('Failed to reorder category:', err);
      tg?.showAlert(err instanceof Error ? err.message : 'Не удалось изменить порядок категории');

      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('error');
      }
    } finally {
      setReorderingCategoryId(null);
    }
  };

  const handleDeleteCategory = async (e: React.MouseEvent, category: Category) => {
    e.stopPropagation(); // Prevent opening category edit page

    tg?.showConfirm(
      `Вы уверены, что хотите удалить категорию "${category.name}"? Все товары этой категории также будут удалены.`,
      async (confirmed) => {
        if (!confirmed) return;

        try {
          setDeletingCategoryId(category.id);
          await adminCategoriesApi.deleteCategory(category.id);

          // Remove category from list
          setCategories((prev) => prev.filter((c) => c.id !== category.id));

          tg?.showAlert('Категория успешно удалена');

          if (tg?.HapticFeedback) {
            tg.HapticFeedback.notificationOccurred('success');
          }
        } catch (err: unknown) {
          tg?.showAlert(err instanceof Error ? err.message : 'Ошибка удаления категории');

          if (tg?.HapticFeedback) {
            tg.HapticFeedback.notificationOccurred('error');
          }
        } finally {
          setDeletingCategoryId(null);
        }
      }
    );
  };

  if (loading) {
    return (
      <div className="page admin-categories-page">
        <div className="admin-categories-header">
          <h1 className="admin-categories-title">Управление категориями</h1>
        </div>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page admin-categories-page">
        <div className="admin-categories-header">
          <h1 className="admin-categories-title">Управление категориями</h1>
        </div>
        <div className="admin-categories-error">
          <p className="admin-categories-error-message">{error}</p>
          <button className="admin-categories-retry-btn" onClick={loadCategories}>
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page admin-categories-page">
      <div className="admin-categories-header">
        <h1 className="admin-categories-title">Управление категориями</h1>
        <button className="admin-categories-add-btn" onClick={handleAddCategory}>
          + Добавить категорию
        </button>
      </div>

      <div className="admin-categories-content">
        {categories.length === 0 ? (
          <div className="admin-categories-empty">
            <p className="admin-categories-empty-message">Категории не найдены</p>
          </div>
        ) : (
          <div className="admin-categories-list">
            {categories.map((category, index) => (
              <div
                key={category.id}
                className="admin-categories-item"
                onClick={() => handleCategoryClick(category.id)}
              >
                <div className="admin-categories-item-info">
                  <h3 className="admin-categories-item-name">{category.name}</h3>
                  <p className="admin-categories-item-description">
                    {category.description || 'Без описания'}
                  </p>
                  <div className="admin-categories-item-meta">
                    <span className={`admin-categories-item-status ${category.is_active ? 'active' : 'inactive'}`}>
                      {category.is_active ? 'Активна' : 'Неактивна'}
                    </span>
                  </div>
                </div>

                <div className="admin-categories-item-actions">
                  <div className="admin-categories-reorder-buttons">
                    <button
                      className="admin-categories-reorder-btn"
                      onClick={(e) => handleReorder(e, category.id, 'up')}
                      disabled={index === 0 || reorderingCategoryId === category.id}
                      title="Переместить вверх"
                    >
                      {reorderingCategoryId === category.id ? (
                        <div className="admin-categories-spinner-small"></div>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M8 4L3 9L4.5 10.5L8 7L11.5 10.5L13 9L8 4Z"
                            fill="currentColor"
                          />
                        </svg>
                      )}
                    </button>
                    <button
                      className="admin-categories-reorder-btn"
                      onClick={(e) => handleReorder(e, category.id, 'down')}
                      disabled={index === categories.length - 1 || reorderingCategoryId === category.id}
                      title="Переместить вниз"
                    >
                      {reorderingCategoryId === category.id ? (
                        <div className="admin-categories-spinner-small"></div>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M8 12L13 7L11.5 5.5L8 9L4.5 5.5L3 7L8 12Z"
                            fill="currentColor"
                          />
                        </svg>
                      )}
                    </button>
                  </div>

                  <button
                    className="admin-categories-delete-btn"
                    onClick={(e) => handleDeleteCategory(e, category)}
                    disabled={deletingCategoryId === category.id}
                  >
                    {deletingCategoryId === category.id ? (
                      <div className="admin-categories-spinner"></div>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path
                          d="M4 5H16M8 8V14M12 8V14M5 5L6 16C6 16.5304 6.21071 17.0391 6.58579 17.4142C6.96086 17.7893 7.46957 18 8 18H12C12.5304 18 13.0391 17.7893 13.4142 17.4142C13.7893 17.0391 14 16.5304 14 16L15 5M7 5V3C7 2.73478 7.10536 2.48043 7.29289 2.29289C7.48043 2.10536 7.73478 2 8 2H12C12.2652 2 12.5196 2.10536 12.7071 2.29289C12.8946 2.48043 13 2.73478 13 3V5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
